import type { UpdateIdentityBody } from "@ory/client"
import axios from "axios"
import type { AxiosResponse } from "axios"

import APIConfig from "./config"
import type {
  Partner,
  PartnerSummary,
  Station,
  User,
  UserKratosIdentity,
  createUsageReportInput,
} from "./types"

export const portalApi = axios.create({
  baseURL: `${APIConfig.portalApi}/portal-api/v1`,
  withCredentials: true,
})

// Intercept 401 responses and redirect to login page.
// Note: error.response is undefined for cancelled requests (AbortController) or
// network failures; guard before reading .status so we don't surface a spurious
// TypeError to callers that already classify cancellations.
portalApi.interceptors.response.use(
  (response) => response, // pass through successful responses
  (error) => {
    if (error?.response?.status === 401) {
      window.location.href = "/login"
    }
    // return error for further handling if needed
    return Promise.reject(error)
  },
)

export const listPartners = async ({
  offset,
  limit,
}: {
  offset: number
  limit: number
}): Promise<Array<Partner>> => {
  const { data } = await portalApi.get(
    `/partners?offset=${offset}&limit=${limit}`,
  )
  return data
}

export const listPartnerIds = async (): Promise<Array<number>> => {
  const { data } = await portalApi.get("/partner_ids")
  return data
}

export const createPartner = async (payload: any): Promise<Partner> => {
  const { data } = await portalApi.post("/partners", payload)
  return data
}

export const getPartner = async (
  partnerId: string,
): Promise<PartnerSummary> => {
  const { data } = await portalApi.get(`/partners/${partnerId}`)
  return data
}

export const updatePartner = async ({
  partnerId,
  payload,
}: {
  partnerId: string
  payload: any
}): Promise<Partner> => {
  const { data } = await portalApi.put(`/partners/${partnerId}`, payload)
  return data
}

export const deletePartner = async (partnerId: string): Promise<void> => {
  await portalApi.delete(`/partners/${partnerId}`)
}

export const whoAmI = async (): Promise<User> => {
  const { data } = await portalApi.get("/users/me")
  return data
}

export const createUser = async (payload: any): Promise<any> => {
  const { data } = await portalApi.post("/users", payload)
  return data
}

export const listUsers = async (
  partnerId?: string,
): Promise<Array<UserKratosIdentity>> => {
  const { data } = await portalApi.get(
    partnerId ? `/users?partnerId=${partnerId}` : "/users",
  )
  return data
}

export const getUser = async (userId: string): Promise<any> => {
  const { data } = await portalApi.get(`/users/${userId}`)
  return data
}

export const updateUser = async ({
  userId,
  payload,
}: {
  userId: string
  payload: UpdateIdentityBody
}): Promise<any> => {
  const { data } = await portalApi.put(`/users/${userId}`, payload)
  return data
}

export const deleteUser = async (userId: string): Promise<any> => {
  const { data } = await portalApi.delete(`/users/${userId}`)
  return data
}

export const getApiKeys = async (): Promise<any> => {
  const { data } = await portalApi.get(`/api-keys`)
  return data
}

export const createAPIKey = async (payload: any): Promise<any> => {
  const { data } = await portalApi.post("/api-keys", payload)
  return data
}

export const deleteApiKey = async (apiKeyId: string): Promise<any> => {
  const { data } = await portalApi.delete(`/api-keys/${apiKeyId}`)
  return data
}

export const generateReport = async (
  orgId: string,
  payload: createUsageReportInput,
): Promise<any> => {
  const { data } = await portalApi.post(`/orgs/${orgId}/reports`, payload)
  return data
}

export const getReport = async (
  orgId: string,
  reportId: string,
  limit: number = 0,
): Promise<AxiosResponse<any, any>> => {
  const limitQuery = limit > 0 ? `?limit=${limit}` : ""
  return portalApi.get(`/orgs/${orgId}/reports/${reportId}${limitQuery}`, {
    headers: {
      "Content-Type": "text/csv",
    },
    responseType: "blob",
  })
}

export const GetColumnsByReportType = async (
  reportType: string,
): Promise<any> => {
  const { data } = await portalApi.get(`/reports/${reportType}/columns`)
  return data
}

// STATIONS_FORMAT_VERSION is the highest /stations binary wire-format version
// this client can decode. Mirrors service.StationsFormatVersion in the
// partnerservice Go code. The server advertises its format in the
// X-Stations-Format-Version response header; a higher value means the server
// changed the record layout in a way this build can't safely read, so we throw
// rather than silently misinterpret the bytes. An absent header means the
// legacy v1 format (18-byte records, no avg_health byte).
export const STATIONS_FORMAT_VERSION = 2

// Minimum per-station record size in bytes, keyed by format version — the fewest
// bytes needed to hold every field this client reads for that version. v1 is the
// legacy 18-byte record; v2 appends a 1-byte avg_health, so 19. The server also
// sends the actual stride in X-Stations-Record-Size; we walk at that stride (so
// a future additive trailing field doesn't misalign us) but require it to be at
// least this minimum. Must match Go's StationPortalRecordSize.
const STATIONS_MIN_RECORD_SIZE: Record<number, number> = { 1: 18, 2: 19 }

// Minimum avg_health percent for a station to count as nominally healthy — the
// gate SQI selects on. Mirrors nominalHealthyMinAvgHealth / sqiHealthyAvgThreshold
// on the backend.
const NOMINAL_HEALTHY_MIN_AVG_HEALTH = 50

export interface StationsResponse {
  stations: Array<Station>
  // ISO 8601 string when the station-health worker last wrote the cache, or
  // null if the backend didn't surface a timestamp.
  gatheredAt: string | null
  // The /stations wire-format version the response was decoded as (1 = legacy
  // health flag, 2 = instant and avg health). The UI can decide which fields
  // are trustworthy (e.g. nominallyHealthy only exists from v2).
  formatVersion: number
}

export const getMapStations = async (): Promise<StationsResponse> => {
  const resp = await portalApi.get<ArrayBuffer>(`/stations`, {
    responseType: "arraybuffer",
  })
  const gatheredHeader =
    resp.headers["x-stations-gathered-at"] ??
    resp.headers["X-Stations-Gathered-At"]
  const versionHeader =
    resp.headers["x-stations-format-version"] ??
    resp.headers["X-Stations-Format-Version"]
  // Absent header => legacy v1 (predates versioning).
  const formatVersion =
    typeof versionHeader === "string" ? Number(versionHeader) : 1
  if (
    !Number.isInteger(formatVersion) ||
    formatVersion < 1 ||
    formatVersion > STATIONS_FORMAT_VERSION
  ) {
    throw new Error(
      `Unsupported /stations format version "${String(
        versionHeader,
      )}"; this app understands up to v${STATIONS_FORMAT_VERSION}. Please refresh.`,
    )
  }

  // Prefer the server-advertised stride; fall back to the known minimum for the
  // version when the header is absent (an old server). Require it to be at least
  // the minimum so every field offset we read stays within each record.
  const recordSizeHeader =
    resp.headers["x-stations-record-size"] ??
    resp.headers["X-Stations-Record-Size"]
  const minRecordSize = STATIONS_MIN_RECORD_SIZE[formatVersion]
  const recordSize =
    typeof recordSizeHeader === "string"
      ? Number(recordSizeHeader)
      : minRecordSize
  if (!Number.isInteger(recordSize) || recordSize < minRecordSize) {
    throw new Error(
      `Invalid /stations record size "${String(
        recordSizeHeader,
      )}" for format v${formatVersion} (need an integer >= ${minRecordSize}).`,
    )
  }

  const view = new DataView(resp.data)
  const stations: Array<Station> = []
  // Each station is a fixed recordSize-byte record (must match Go
  // EncodeStations): lat float64 (BE) | lon float64 (BE) | health uint8 | kinds
  // uint8 [| avg_health uint8 in v2]. Walk the buffer recordSize bytes at a
  // time, reading each field by offset. The health byte is a plain 0/1
  // instantaneously-healthy flag in every version. Kinds bits: bit 0 = NX,
  // bit 1 = AM-modeling. v2 appends avg_health (rolling-window health percent),
  // from which nominal health is derived.
  for (let o = 0; o + recordSize <= view.byteLength; o += recordSize) {
    const kinds = view.getUint8(o + 17)
    const station: Station = {
      lat: view.getFloat64(o, false), // false = big-endian
      lon: view.getFloat64(o + 8, false),
      healthy: view.getUint8(o + 16) === 1,
      is_nx: (kinds & 0x01) !== 0,
      is_am_modeling: (kinds & 0x02) !== 0,
    }
    // avg_health / nominal health only exist from v2 on; leave them undefined
    // for v1 so callers can distinguish "unknown" from "known unhealthy".
    if (formatVersion >= 2) {
      const avgHealth = view.getUint8(o + 18)
      station.avgHealth = avgHealth
      station.nominallyHealthy = avgHealth >= NOMINAL_HEALTHY_MIN_AVG_HEALTH
    }
    stations.push(station)
  }
  return {
    stations,
    gatheredAt: typeof gatheredHeader === "string" ? gatheredHeader : null,
    formatVersion,
  }
}

export interface SqiGridRequest {
  min_lat: number
  max_lat: number
  min_lon: number
  max_lon: number
  // Requested cell size in Web Mercator (EPSG:3857) meters. The server snaps
  // to a power-of-2 zoom ladder so the underlying sample lattice is anchored
  // globally — bboxes and zoom levels never shift the SQI evaluation points.
  resolution_m: number
}

export interface SqiGrid {
  // Snapped bbox (expanded outward to whole lattice pixels). May be slightly
  // larger than requested; client should use these values to position the
  // BitmapLayer.
  min_lat: number
  max_lat: number
  min_lon: number
  max_lon: number
  // Actual cell size used, in Web Mercator meters.
  resolution_m: number
  width: number
  height: number
  // Row-major. row 0 = min_lat. 0-100, or -1 = no coverage.
  values: Array<number>
}

export interface Features {
  // Whether the Service Quality Indicator coverage layer is enabled. Mirrors
  // the backend FEATURE_SQI flag so the UI gates SQI on the same runtime state.
  sqi: boolean
  // Whether the logged-in user's email domain is allowlisted for SQI debug.
  sqiDebugAllowed: boolean
}

// getFeatures returns the backend's runtime feature-flag state. Ungated on the
// server, so it succeeds regardless of which features are enabled.
export const getFeatures = async (): Promise<Features> => {
  const { data } = await portalApi.get(`/features`)
  return data
}

export const getSqiGrid = async (
  req: SqiGridRequest,
  signal?: AbortSignal,
): Promise<SqiGrid> => {
  const { data } = await portalApi.post(`/sqi_grid`, req, { signal })
  return data
}

// getSqiScore returns the scalar SQI score for one lat/lon. Available to every
// logged-in portal user; no station coords or SQA subscores are exposed. sqi is
// null when the point is outside the coverage footprint or otherwise unscorable
// (the endpoint reports absence as null rather than a sentinel).
export const getSqiScore = async (
  latitude: number,
  longitude: number,
): Promise<{ sqi: number | null }> => {
  const { data } = await portalApi.post(`/service_quality`, {
    latitude,
    longitude,
  })
  return data
}

// getServiceQualityRaw returns the full SQI response for one lat/lon as a
// proto-correct JSON object (via the server's protojson marshal). Used by the
// coverage-map ?debug overlay. Backed by /service_quality_detail, which is
// gated to the SqiDetailAccessEmailDomains allowlist (RequireEmailDomainInAllowlist) —
// non-allowlisted callers get 403.
export const getServiceQualityRaw = async (
  latitude: number,
  longitude: number,
  detailLevel: "" | "standard" | "extended" = "extended",
): Promise<unknown> => {
  const { data } = await portalApi.post(`/service_quality_detail`, {
    latitude,
    longitude,
    detail_level: detailLevel,
  })
  return data
}
